import { supabase } from "../lib/supabase";

export const REQUEST_TYPES = {
  deferment_resumption: {
    label: "Deferment / resumption",
    shortLabel: "Deferment or resumption",
    description: "Pause your studies or request a return to active study.",
    route: "Registrar Academic / Deputy Principal Academic Affairs",
  },
  programme_transfer: {
    label: "Programme transfer",
    shortLabel: "Programme transfer",
    description: "Request to move to another programme through Admissions.",
    route: "Admissions Office",
  },
  late_registration: {
    label: "Late registration request",
    shortLabel: "Late registration",
    description: "Request academic approval to register after the deadline.",
    route: "Registrar Academic / Deputy Principal Academic Affairs",
  },
  withdrawal: {
    label: "Withdrawal request",
    shortLabel: "Withdrawal",
    description: "Request to withdraw from the institution or programme.",
    route: "Registrar Academic / Deputy Principal Academic Affairs",
  },
};

export const REQUEST_STAGES = {
  hod_review: {
    label: "HOD / HOS recommendation",
    description: "Head of Department or Head of Section",
    tone: "amber",
  },
  academic_review: {
    label: "Academic review",
    description: "Registrar Academic / Deputy Principal Academic Affairs",
    tone: "indigo",
  },
  admissions_review: {
    label: "Admissions review",
    description: "Admissions Office",
    tone: "violet",
  },
  finance_review: {
    label: "Finance action / noting",
    description: "Finance receives the approved request for action or noting",
    tone: "emerald",
  },
  completed: {
    label: "Completed",
    description: "The request has been actioned",
    tone: "green",
  },
};

const isMissingWorkflowTable = (error) =>
  error?.code === "PGRST205" || /student_requests|student_request_actions/i.test(error?.message || "");

const normalizeError = (error) => isMissingWorkflowTable(error)
  ? "The student request workflow is not installed in Supabase yet. Apply supabase/migrations/20260926000001_student_request_workflow.sql, then reload this page."
  : error?.message || "Unable to save this request";

const throwWorkflowError = (error) => {
  if (isMissingWorkflowTable(error)) throw new Error(normalizeError(error));
  throw error;
};

const resolveReviewerScope = async (departmentId, actorId, sectionId) => {
  const departmentIds = new Set([departmentId].filter(Boolean).map(String));
  const sectionIds = new Set([sectionId].filter(Boolean).map(String));
  if (!actorId) return { departmentIds: [...departmentIds], sectionIds: [...sectionIds] };

  const [{ data: assignedDepartments }, { data: assignedSections }, { data: headedSections }] = await Promise.all([
    supabase.from("departments").select("id").eq("hod_id", actorId),
    supabase.from("sections").select("id, department_id").eq("hos_id", actorId),
    supabase.from("sections").select("id, department_id").eq("head_id", actorId),
  ]);
  (assignedDepartments || []).forEach((department) => department?.id && departmentIds.add(String(department.id)));
  [...(assignedSections || []), ...(headedSections || [])].forEach((section) => {
    if (section?.id) sectionIds.add(String(section.id));
    if (section?.department_id) departmentIds.add(String(section.department_id));
  });
  return { departmentIds: [...departmentIds], sectionIds: [...sectionIds] };
};

const enrichRequestsWithStudentDetails = async (requests) => {
  const rows = requests || [];
  const studentIds = [...new Set(rows.map((request) => request?.student_id).filter(Boolean).map(String))];
  if (!studentIds.length) return rows;

  // The request relation only exposes the user-profile identity. The scoped
  // student RPC adds admission number, programme, academic level, and module
  // fields while respecting the reviewer's institutional scope.
  let { data, error } = await supabase.rpc("get_scoped_students");
  if (error) {
    const fallback = await supabase
      .from("students")
      .select("*, program:program_id(id, name, code, department_id, section_id)")
      .in("profile_id", studentIds);
    if (!fallback.error) {
      data = fallback.data;
    } else {
      console.warn("Student identifiers could not be loaded for request queue:", error);
      return rows;
    }
  }
  const studentsByProfile = new Map(
    (data || [])
      .map((row) => row?.student || row)
      .filter(Boolean)
      .map((student) => [String(student.profile_id || student.id), student])
  );
  const programIds = [...new Set(
    [...studentsByProfile.values()]
      .filter((student) => student.program_id && !student.program)
      .map((student) => String(student.program_id))
  )];
  const programsById = new Map();
  if (programIds.length) {
    const { data: programs } = await supabase
      .from("programs")
      .select("id, name, code, department_id, section_id")
      .in("id", programIds);
    (programs || []).forEach((program) => programsById.set(String(program.id), program));
  }
  return rows.map((request) => {
    const student = studentsByProfile.get(String(request.student_id));
    if (!student) return request;
    return {
      ...request,
      student: {
        ...(request.student || {}),
        ...student,
        program: student.program || programsById.get(String(student.program_id)) || request.student?.program || null,
      },
    };
  });
};

export const studentRequestService = {
  async listMine(studentId) {
    const { data, error } = await supabase
      .from("student_requests")
      .select("*, student:student_id(id, full_name, email, student_id)")
      .eq("student_id", studentId)
      .order("created_at", { ascending: false });
    if (error) throwWorkflowError(error);
    return enrichRequestsWithStudentDetails(data || []);
  },

  async listQueue(stage, departmentId, actorId, sectionId) {
    const createQuery = (currentStageOnly = true) => {
      let query = supabase
        .from("student_requests")
        .select("*, student:student_id(id, full_name, email, student_id)")
        .order("created_at", { ascending: false });
      if (currentStageOnly && stage && stage !== "all") query = query.eq("current_stage", stage);
      return query;
    };
    const auditField = {
      hod_review: "hod_reviewer_id",
      academic_review: "academic_reviewer_id",
      admissions_review: "admissions_reviewer_id",
      finance_review: "finance_reviewer_id",
    }[stage];
    const createAuditQuery = () => auditField ? createQuery(false).not(auditField, "is", null) : null;
    const createNotingQuery = () => stage && stage !== "all"
      ? createQuery(false).in("status", ["approved", "rejected"])
      : null;
    const mergeResults = (results) => {
      const errors = results.map((result) => result.error).filter(Boolean);
      if (errors.length) throwWorkflowError(errors[0]);
      const unique = new Map();
      results.flatMap((result) => result.data || []).forEach((request) => unique.set(request.id, request));
      return [...unique.values()].sort((left, right) => String(left.created_at || "").localeCompare(String(right.created_at || "")));
    };

    // Department and section assignments are stored on the hierarchy records
    // (departments.hod_id / sections.hos_id), while older staff profiles may
    // not have department_id populated. Resolve both sources so HOD/HOS users
    // still receive requests routed to their department.
    if (stage === "hod_review") {
      const { departmentIds, sectionIds } = await resolveReviewerScope(departmentId, actorId, sectionId);
      if (departmentIds.length || sectionIds.length) {
        const queries = [];
        if (departmentIds.length) {
          queries.push(createQuery().in("department_id", departmentIds));
          if (auditField) queries.push(createAuditQuery().in("department_id", departmentIds));
          queries.push(createNotingQuery().in("department_id", departmentIds));
        }
        if (sectionIds.length) {
          queries.push(createQuery().in("section_id", sectionIds));
          if (auditField) queries.push(createAuditQuery().in("section_id", sectionIds));
          queries.push(createNotingQuery().in("section_id", sectionIds));
        }
        return enrichRequestsWithStudentDetails(mergeResults(await Promise.all(queries)));
      }
    }

    const queries = [createQuery()];
    if (auditField) queries.push(createAuditQuery());
    if (createNotingQuery()) queries.push(createNotingQuery());
    return enrichRequestsWithStudentDetails(mergeResults(await Promise.all(queries)));
  },

  async listDepartmentNotifications(departmentId, actorId, sectionId) {
    const { departmentIds } = await resolveReviewerScope(departmentId, actorId, sectionId);
    let query = supabase
      .from("student_request_notifications")
      .select("*, request:request_id(id, request_number, request_type, status)")
      .is("read_at", null)
      .order("created_at", { ascending: false });
    if (departmentIds.length) {
      query = query.in("department_id", departmentIds);
    } else if (actorId) {
      const { data: viewer } = await supabase
        .from("user_profiles")
        .select("primary_role, secondary_roles")
        .eq("id", actorId)
        .maybeSingle();
      const roles = [viewer?.primary_role, ...(viewer?.secondary_roles || [])].filter(Boolean).map(role => String(role).toLowerCase());
      const institutionWide = [
        "system_administrator", "chief_principal", "vice_chancellor", "vc", "principal",
        "registrar_academic", "deputy_principal_academic", "admissions_officer", "finance_officer",
      ].some(role => roles.includes(role));
      if (!institutionWide) return [];
    } else {
      return [];
    }
    const { data, error } = await query;
    if (error) throwWorkflowError(error);
    return data || [];
  },

  async getActions(requestId) {
    const { data, error } = await supabase
      .from("student_request_actions")
      .select("*, actor:actor_id(id, full_name, email)")
      .eq("request_id", requestId)
      .order("created_at", { ascending: true });
    if (error) throwWorkflowError(error);
    return data || [];
  },

  async getLetterUrl(path) {
    if (!path) return null;
    // Request an inline signed URL so attachments can be previewed in the
    // application instead of being treated as download links by the browser.
    const { data, error } = await supabase.storage.from("documents").createSignedUrl(path, 3600, { download: false });
    if (error) throw error;
    return data?.signedUrl || null;
  },

  async submit({ requestType, payload, reason }) {
    const { data, error } = await supabase.rpc("submit_student_request", {
      p_request_type: requestType,
      p_request_payload: payload || {},
      p_reason: reason || null,
    });
    if (error) throw new Error(normalizeError(error));
    return data;
  },

  async decide(requestId, decision, note) {
    const { data, error } = await supabase.rpc("decide_student_request", {
      p_request_id: requestId,
      p_decision: decision,
      p_note: note || null,
    });
    if (error) throw new Error(normalizeError(error));
    return data;
  },
};

export default studentRequestService;
